/**
 * What the family counts in: money (in a currency of their choosing), marbles,
 * or a unit of their own ("stars", "points", "screen minutes").
 *
 * Switching changes how amounts are SHOWN and whether parts of one may be
 * typed; nothing already recorded is rewritten (services/api/valley/units.py).
 * One of anything is one of the next -- $1 becomes 1 marble -- which the
 * section says, and it warns when somebody already holds part of a unit and
 * the new one is whole.
 */

import { useState } from 'react';
import {
  CURRENCIES,
  dollars,
  isWhole,
  type Unit,
} from '../family/money';
import { saveUnit, type Person } from '../family/state';
import { MarbleJar } from '../valley/MarbleJar';

type Kind = Unit['type'];

export function Units({
  familyId,
  familyKey,
  people,
  unit,
  onSaved,
}: {
  familyId: string;
  familyKey: CryptoKey;
  people: Person[];
  unit: Unit;
  onSaved: () => void;
}) {
  const [kind, setKind] = useState<Kind>(unit.type);
  const [currency, setCurrency] = useState(unit.type === 'currency' ? unit.currency : 'USD');
  const [one, setOne] = useState(unit.type === 'custom' ? unit.one : '');
  const [many, setMany] = useState(unit.type === 'custom' ? unit.many : '');
  const [icon, setIcon] = useState(unit.type === 'custom' ? unit.icon : '⭐');
  const [whole, setWhole] = useState(unit.type === 'custom' ? unit.whole : true);
  const [working, setWorking] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const next: Unit =
    kind === 'currency'
      ? { type: 'currency', currency }
      : kind === 'marbles'
        ? { type: 'marbles' }
        : { type: 'custom', one: one.trim(), many: many.trim() || one.trim(), icon: icon.trim(), whole };
  const ready = kind !== 'custom' || one.trim() !== '';
  const changed = JSON.stringify(next) !== JSON.stringify(unit);
  // Part of a unit held by somebody, and the new unit is whole: say so.
  const parts = isWhole(next) && people.some((p) => p.held % 100 !== 0);

  function save(): void {
    setWorking(true);
    setError('');
    setSaved(false);
    void saveUnit(familyId, familyKey, next, people)
      .then(() => {
        setSaved(true);
        onSaved();
      })
      .catch((problem: unknown) => setError(problem instanceof Error ? problem.message : 'That did not save.'))
      .finally(() => setWorking(false));
  }

  return (
    <section id="units" aria-busy={working}>
      <p className="field-label">What your family counts in</p>
      <div className="unit-choices" role="radiogroup" aria-label="What your family counts in">
        {(
          [
            ['currency', 'Money', '$ € £'],
            ['marbles', 'Marbles', 'in a jar'],
            ['custom', 'Your own', 'stars, points…'],
          ] as Array<[Kind, string, string]>
        ).map(([id, title, hint]) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={kind === id}
            className={kind === id ? 'unit-choice on' : 'unit-choice'}
            onClick={() => {
              setKind(id);
              setSaved(false);
            }}
          >
            <strong>{title}</strong>
            <span>{hint}</span>
          </button>
        ))}
      </div>

      {kind === 'currency' ? (
        <label className="field">
          <span>Currency</span>
          <select value={currency} onChange={(event) => setCurrency(event.target.value)}>
            {CURRENCIES.map(([code, name]) => (
              <option key={code} value={code}>
                {name} ({dollars(1250, { type: 'currency', currency: code })})
              </option>
            ))}
          </select>
          <small>Shown the way this device writes money in that currency.</small>
        </label>
      ) : null}

      {kind === 'marbles' ? (
        <div className="unit-marbles">
          <MarbleJar count={7} from={0} size={90} />
          <p className="footnote">
            Whole marbles only. Every chore drops its marbles into the child&rsquo;s jar, one at a
            time, and the valley shows the family&rsquo;s jar beside its number.
          </p>
        </div>
      ) : null}

      {kind === 'custom' ? (
        <div className="unit-custom">
          <label className="field">
            <span>One of them is called</span>
            <input value={one} onChange={(event) => setOne(event.target.value)} maxLength={24} placeholder="star" autoComplete="off" />
          </label>
          <label className="field">
            <span>More than one are called</span>
            <input value={many} onChange={(event) => setMany(event.target.value)} maxLength={24} placeholder={one ? `${one}s` : 'stars'} autoComplete="off" />
          </label>
          <label className="field">
            <span>A picture to go with it (optional)</span>
            <input value={icon} onChange={(event) => setIcon(event.target.value)} maxLength={4} placeholder="⭐" autoComplete="off" />
            <small>An emoji works well. Encrypted with the name before it leaves this device.</small>
          </label>
          <label className="allowance-toggle">
            <input type="checkbox" checked={!whole} onChange={(event) => setWhole(!event.target.checked)} />
            <span>Allow parts of one, like 1.5</span>
          </label>
        </div>
      ) : null}

      {ready ? (
        <p className="footnote">
          Looks like: <strong>{dollars(1200, next)}</strong>, <strong>{dollars(100, next)}</strong>
        </p>
      ) : null}
      <p className="footnote">
        Nothing already recorded changes: one of the old unit is one of the new, so{' '}
        {dollars(100, unit)} becomes {ready ? dollars(100, next) : 'one of the new'}. Allowance,
        goals and shortcuts carry on in the new unit.
      </p>
      {parts ? (
        <p className="footnote">
          <strong>Heads up:</strong> somebody holds part of one ({people.filter((p) => p.held % 100 !== 0).map((p) => `${p.name || 'Somebody'} ${dollars(p.held, next)}`).join(', ')}). That stays as it is; everything new is whole.
        </p>
      ) : null}

      {error ? <p className="error">{error}</p> : null}
      {saved && !changed ? <p className="footnote">Saved.</p> : null}
      <div className="form-actions">
        <button
          type="button"
          className="secondary"
          disabled={!changed || working}
          onClick={() => {
            // Back to what is saved: whatever was being changed is dropped.
            setKind(unit.type);
            setCurrency(unit.type === 'currency' ? unit.currency : 'USD');
            setOne(unit.type === 'custom' ? unit.one : '');
            setMany(unit.type === 'custom' ? unit.many : '');
            setIcon(unit.type === 'custom' ? unit.icon : '⭐');
            setWhole(unit.type === 'custom' ? unit.whole : true);
            setError('');
          }}
        >
          Cancel
        </button>
        <button aria-busy={working} type="button" className="primary" disabled={!ready || !changed || working} onClick={save}>
          {working ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  );
}
